import { TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  useCrudEditContext,
  useDropdownSourceContext,
  useDynamicFormsTranslate,
} from "@ryde/redux";
import { ICustomField, IDropdownItem } from "@ryde/shared";

import { DropdownBoundTemplate } from "./dropdown-bound.template";
import {
  resolveLockedSiteLabel,
  shouldLockForeignSiteAsReadonly,
} from "./site-dropdown-bound.utils";

interface ISiteDropdownBoundTemplateProps extends ICustomField {
  onSelect?: (value?: IDropdownItem) => void;
  size?: "small" | "medium";
  onBlur?: (formOnBlur: () => void) => void;
}

export const SiteDropdownBoundTemplate: FC<ISiteDropdownBoundTemplateProps> = ({
  name,
  formLabel,
  labelTranslated,
  formAttributes,
  e2eData = "site-dropdown-bound-template",
  ...props
}) => {
  const { watch } = useFormContext();
  const { getDropdownItems } = useDropdownSourceContext();
  const { data } = useCrudEditContext<{ siteName?: string }>();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });

  const currentSiteId = watch(name);
  const siteNameFromForm = watch("siteName");
  const [allowedItems, setAllowedItems] = useState<
    IDropdownItem[] | undefined
  >();

  useEffect(() => {
    let isCancelled = false;

    const loadAssignedSites = async (): Promise<void> => {
      const items = (await getDropdownItems?.("site")) ?? [];

      if (!isCancelled) {
        setAllowedItems(items);
      }
    };

    void loadAssignedSites();

    return () => {
      isCancelled = true;
    };
  }, [getDropdownItems]);

  const siteFormAttributes = useMemo(
    () => ({
      ...(formAttributes ?? {}),
      dropItemsSource: formAttributes?.dropItemsSource || "site",
    }),
    [formAttributes],
  );

  const allowedSiteIds = allowedItems?.map(item => item.value);
  const lockAsReadonly = shouldLockForeignSiteAsReadonly({
    currentSiteId,
    allowedSiteIds,
  });
  const lockedLabel = resolveLockedSiteLabel({
    siteName: siteNameFromForm || data?.dto?.siteName,
    currentSiteId,
    allowedItems,
  });

  if (lockAsReadonly) {
    return (
      <TextField
        value={lockedLabel}
        label={formLabelTranslated}
        disabled
        fullWidth
        data-e2e="site-dropdown-bound-locked"
      />
    );
  }

  return (
    <DropdownBoundTemplate
      {...props}
      name={name}
      formLabel={formLabel}
      labelTranslated={labelTranslated}
      formAttributes={siteFormAttributes}
      e2eData={e2eData}
    />
  );
};
